import { defaultInvoiceCustomStyle } from "../constants/defaultInvoiceCustomStyle";
import type { InvoiceBorderStyle, InvoiceCustomStyle, InvoiceFontFamily, InvoiceIconName } from "../types/invoiceCustomStyle.types";

const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const FONT_FAMILIES: InvoiceFontFamily[] = ["Inter", "Roboto", "Lato", "Montserrat", "Merriweather", "Source Sans 3"];

const ICON_NAMES: InvoiceIconName[] = ["default", "calendar", "cleaning", "document", "location", "assignment", "payment", "none"];

const BORDER_STYLES: InvoiceBorderStyle[] = ["solid", "dashed"];

export const isInvoiceHexColor = (value: unknown): value is string => typeof value === "string" && HEX_COLOR_PATTERN.test(value.trim());

export const invoiceHexToRgbTuple = (value: unknown, fallback: [number, number, number] = [248, 249, 250]): [number, number, number] => {
  if (!isInvoiceHexColor(value)) return fallback;

  const raw = value.trim().replace("#", "");
  const hex = raw.length === 3 ? `${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}` : raw;

  return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const clampInt = (value: unknown, min: number, max: number, fallback: number): number => {
  const parsed = typeof value === "number" ? value : Number(value);

  if (!Number.isFinite(parsed)) return fallback;

  return Math.round(Math.min(max, Math.max(min, parsed)));
};

const pickFrom = <T extends string>(allowed: T[], value: unknown, fallback: T): T => (allowed.includes(value as T) ? (value as T) : fallback);

export const normalizeInvoiceCustomStyle = (raw: unknown): InvoiceCustomStyle => {
  const source = asRecord(raw);

  if (!source) return structuredClone(defaultInvoiceCustomStyle);

  const colors = asRecord(source.colors);
  const typography = asRecord(source.typography);
  const borders = asRecord(source.borders);
  const spacing = asRecord(source.spacing);
  const icons = asRecord(source.icons);
  const labels = asRecord(source.labels);

  const pickColor = (key: keyof InvoiceCustomStyle["colors"]): string => {
    const value = colors?.[key];

    return isInvoiceHexColor(value) ? value.trim().toUpperCase() : defaultInvoiceCustomStyle.colors[key];
  };

  const pickIcon = (key: keyof InvoiceCustomStyle["icons"]): InvoiceIconName =>
    pickFrom(ICON_NAMES, icons?.[key], defaultInvoiceCustomStyle.icons[key]);

  return {
    colors: {
      pageBackground: pickColor("pageBackground"),
      cardBackground: pickColor("cardBackground"),
      emailHeader: pickColor("emailHeader"),
      title: pickColor("title"),
      subTitle: pickColor("subTitle"),
      body: pickColor("body"),
      border: pickColor("border"),
      total: pickColor("total"),
      icon: pickColor("icon"),
    },

    typography: {
      fontFamily: pickFrom(FONT_FAMILIES, typography?.fontFamily, defaultInvoiceCustomStyle.typography.fontFamily),
      headingFontFamily: pickFrom(FONT_FAMILIES, typography?.headingFontFamily, defaultInvoiceCustomStyle.typography.headingFontFamily),
    },

    borders: {
      color: isInvoiceHexColor(borders?.color) ? String(borders?.color).trim().toUpperCase() : defaultInvoiceCustomStyle.borders.color,
      width: clampInt(borders?.width, 0, 4, defaultInvoiceCustomStyle.borders.width),
      radius: clampInt(borders?.radius, 0, 32, defaultInvoiceCustomStyle.borders.radius),
      style: pickFrom(BORDER_STYLES, borders?.style, defaultInvoiceCustomStyle.borders.style),
    },

    spacing: {
      cardGap: clampInt(spacing?.cardGap, 0, 32, defaultInvoiceCustomStyle.spacing.cardGap),
    },

    icons: {
      service: pickIcon("service"),
      visits: pickIcon("visits"),
      location: pickIcon("location"),
      notes: pickIcon("notes"),
      payment: pickIcon("payment"),
      additionalCharges: pickIcon("additionalCharges"),
    },

    labels: {
      increasePercentageByFrequency:
        typeof labels?.increasePercentageByFrequency === "string" && labels.increasePercentageByFrequency.trim().length > 0
          ? labels.increasePercentageByFrequency.trim()
          : defaultInvoiceCustomStyle.labels?.increasePercentageByFrequency ?? "Service fee",
    },
  };
};

export const toInvoiceCustomStylePayload = (style: InvoiceCustomStyle): InvoiceCustomStyle => normalizeInvoiceCustomStyle(style);

export const resolveInvoiceCustomStyleFromInvoice = (invoiceDetails: Record<string, any> | null | undefined): InvoiceCustomStyle => {
  if (!invoiceDetails || invoiceDetails.type === "service_usage") return structuredClone(defaultInvoiceCustomStyle);

  const billingEvents = Array.isArray(invoiceDetails.billingJobEvents) ? invoiceDetails.billingJobEvents : [];

  const stored =
    billingEvents[0]?.job?.company?.invoiceCustomStyle ??
    invoiceDetails?.jobEvent?.job?.company?.invoiceCustomStyle ??
    invoiceDetails?.quote?.company?.invoiceCustomStyle ??
    invoiceDetails?.company?.invoiceCustomStyle ??
    null;

  return normalizeInvoiceCustomStyle(stored);
};
